import { useEffect, useState } from "react"
import { useCountdown } from "../../hooks"
import { Keyboard, Platform, ScrollView } from "react-native"
import { ButtonV2, KeyboardAvoidingView, Spacing, Text } from "../../ui-components"
import { OnboardingScreenWrapper } from "./OnboardingScreenWrapper.tsx"
import { OTPInputV2 } from "../OTPInputV2.tsx"
import styled from "@emotion/native"
import { formatOtpResendCountdownLabel } from "../../utils/otpResendCountdown"

type NumberVerificationScreenUIProps = {
  proceed: (otpCode: string) => Promise<void>
  phoneNumber: string
  /** Always provided by phoneAuthMachine flows (RESEND). */
  onResendCode: () => Promise<void>
  isVerifying?: boolean
  verificationFailed?: boolean
  isResending?: boolean
  onBack?: () => void
}

export function NumberVerificationScreenUI({
  proceed,
  phoneNumber,
  onResendCode,
  isVerifying,
  verificationFailed,
  isResending,
  onBack,
}: NumberVerificationScreenUIProps) {
  const [otpCode, setOTPCode] = useState("")
  const isPhoneVerificationDataEnabled = otpCode.length === 6
  const { startCountdown, timeRemaining } = useCountdown()

  useEffect(() => {
    return startCountdown()
  }, [startCountdown])

  async function resendCode() {
    await onResendCode()
    startCountdown()
  }

  const errorMessage =
    otpCode.length >= 6 && verificationFailed && !isVerifying
      ? "Invalid code. Please try again."
      : ""

  function renderResendCodeLabel() {
    if (timeRemaining) {
      return (
        <Text fontSize={18} lineHeight={28} fontFamily="IBMPlexSans-Regular" color="gray900">
          {/* One string, one DOM text node. Browsers that rewrite page text (translation,
              phone linkifiers) detach React's text nodes, and the per-second update then
              throws NotFoundError. See ENG-524. */}
          {formatOtpResendCountdownLabel(timeRemaining)}
        </Text>
      )
    }

    return (
      <Text
        fontSize={18}
        lineHeight={28}
        fontFamily="IBMPlexSans-SemiBold"
        color="royalBlue"
        onPress={resendCode}
        disabled={!!isResending}
      >
        Resend code
      </Text>
    )
  }

  return (
    <KeyboardAvoidingView keyboardVerticalOffset={0} testID="otpScreen">
      <OnboardingScreenWrapper stepNumber={2} onBack={onBack}>
        <ContentContainer>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ flexGrow: 1 }}
            alwaysBounceVertical={false}
            keyboardShouldPersistTaps="always"
          >
            <StyledPressable onPress={Keyboard.dismiss}>
              <Container>
                <UpperContent>
                  <Spacing top={44} />
                  <Text type="h1">Verify your number</Text>
                  <Spacing top={12} />
                  <Text
                    fontSize={20}
                    lineHeight={32}
                    fontFamily="IBMPlexSans-Regular"
                    color="gray900"
                  >
                    {/* One text node: browsers underline phone numbers as links (ENG-524). */}
                    {`A code was sent to ${phoneNumber}`}
                  </Text>
                  <Spacing top={40} />
                  <OTPInputV2
                    code={otpCode}
                    setCode={setOTPCode}
                    onProceed={proceed}
                    errorMessage={errorMessage}
                  />
                  <Spacing top={16} />
                  {renderResendCodeLabel()}
                </UpperContent>
                <ButtonWrapper>
                  <ButtonV2
                    title="Continue"
                    onPress={() => proceed(otpCode)}
                    variant="blue2"
                    disabled={!isPhoneVerificationDataEnabled}
                    loading={!!isVerifying}
                    testID="otpContinueButton"
                  />
                </ButtonWrapper>
              </Container>
            </StyledPressable>
          </ScrollView>
        </ContentContainer>
      </OnboardingScreenWrapper>
    </KeyboardAvoidingView>
  )
}

const ContentContainer = styled.View`
  flex: 1;
  max-width: 500px;
  width: 100%;
  align-self: center;
`

const ButtonWrapper = styled.View`
  margin-left: 20px;
  margin-right: 20px;
  padding-bottom: ${() => (Platform.OS === "web" ? "20px" : 0)};
`

const StyledPressable = styled.Pressable`
  flex: 1;
`

const UpperContent = styled.View`
  margin-left: 20px;
  margin-right: 20px;
`

const Container = styled.View`
  flex: 1;
  justify-content: space-between;
`
